import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiUsers, 
  FiCalendar, 
  FiPlus, 
  FiKey, 
  FiClock, 
  FiSearch,
  FiUser,
  FiBell,
  FiMapPin,
  FiArrowRight
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Base empty state component
function EmptyStateBase({ 
  icon,
  title,
  description,
  actions = [],
  illustration,
  className = '',
  animated = true
}) {
  const Container = animated ? motion.div : 'div';
  const animationProps = animated ? {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  } : {};

  return (
    <Container
      {...animationProps}
      className={`text-center py-12 px-6 space-y-6 ${className}`}
    >
      {/* Illustration or Icon */}
      {illustration ? (
        <div className="w-32 h-32 mx-auto mb-6">
          {illustration}
        </div>
      ) : icon ? (
        <motion.div
          initial={animated ? { scale: 0 } : {}}
          animate={animated ? { scale: 1 } : {}}
          transition={animated ? { delay: 0.2, type: "spring", stiffness: 200 } : {}}
          className="w-20 h-20 bg-surface rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <SafeIcon icon={icon} className="w-10 h-10 text-text-muted" />
        </motion.div>
      ) : null}

      {/* Content */}
      <div className="space-y-3 max-w-md mx-auto">
        <motion.h3
          initial={animated ? { opacity: 0 } : {}}
          animate={animated ? { opacity: 1 } : {}}
          transition={animated ? { delay: 0.3 } : {}}
          className="text-h3 text-text-primary"
        >
          {title}
        </motion.h3>
        <motion.p
          initial={animated ? { opacity: 0 } : {}}
          animate={animated ? { opacity: 1 } : {}}
          transition={animated ? { delay: 0.4 } : {}}
          className="text-body text-text-secondary"
        >
          {description}
        </motion.p>
      </div>

      {/* Actions */}
      {actions.length > 0 && (
        <motion.div
          initial={animated ? { opacity: 0, y: 20 } : {}}
          animate={animated ? { opacity: 1, y: 0 } : {}}
          transition={animated ? { delay: 0.5 } : {}}
          className="space-y-3"
        >
          {actions.map((action, index) => {
            const isLink = !!action.to;
            const actionProps = isLink 
              ? { to: action.to } 
              : { onClick: action.onClick };

            const motionProps = isLink 
              ? {} 
              : { whileHover: { scale: 1.02 }, whileTap: { scale: 0.98 } };

            const ActionWrapper = isLink ? Link : motion.button;

            return (
              <ActionWrapper
                key={index}
                {...actionProps}
                {...motionProps}
                className={`
                  inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg
                  text-label transition-all duration-200 min-h-[44px]
                  ${action.variant === 'primary'
                    ? 'bg-accent !text-white hover:bg-interactive-hover '
                    : action.variant === 'secondary'
                      ? 'bg-surface text-text-secondary hover:bg-card'
                      : 'border border-border-subtle text-text-secondary hover:bg-surface'
                  }
                  ${action.fullWidth ? 'w-full' : ''}
                `}
              >
                {action.icon && <SafeIcon icon={action.icon} className="w-4 h-4" />}
                <span>{action.label}</span>
                {action.showArrow && <SafeIcon icon={FiArrowRight} className="w-4 h-4" />}
              </ActionWrapper>
            );
          })}
        </motion.div>
      )}
    </Container>
  );
}

// Specific empty states for TeeSync
export function EmptyGroups({ onCreateGroup, onJoinGroup }) {
  return (
    <EmptyStateBase
      icon={FiUsers}
      title="No golf groups yet"
      description="Join your friends or create a new group to start organising golf rounds and tracking availability."
      actions={[
        {
          label: "Create Group",
          icon: FiPlus,
          variant: "primary",
          onClick: onCreateGroup,
          to: "/create-group"
        },
        {
          label: "Join Group",
          icon: FiKey,
          variant: "outline",
          onClick: onJoinGroup
        }
      ]}
    />
  );
}

export function EmptyRounds({ groupId }) {
  const actions = [
    {
      label: "New Round",
      icon: FiCalendar,
      variant: "primary",
      to: groupId ? `/create-round/${groupId}` : "/create-round"
    }
  ];

  if (!groupId) {
    actions.push({
      label: "Set Availability",
      icon: FiClock,
      variant: "outline",
      to: "/my-availability"
    });
  }

  return (
    <EmptyStateBase
      icon={FiCalendar}
      title="No rounds scheduled"
      description={
        groupId 
          ? "Get the group together! Schedule your next golf round and see who's available."
          : "Start planning your golf games. Schedule rounds and coordinate with your groups."
      }
      actions={actions}
    />
  );
}

export function EmptyMembers({ groupId, onInviteMembers }) {
  return (
    <EmptyStateBase
      icon={FiUser}
      title="Just you in this group"
      description="Invite your golf buddies to join the group so you can start scheduling rounds together."
      actions={[
        {
          label: "Invite Members",
          icon: FiPlus,
          variant: "primary",
          onClick: onInviteMembers
        }
      ]}
    />
  );
}

export function EmptyNotifications() {
  return (
    <EmptyStateBase
      icon={FiBell}
      title="All caught up!"
      description="You don't have any new notifications. We'll let you know when there's news about your golf groups and rounds."
      actions={[
        {
          label: "Back to Dashboard",
          icon: FiArrowRight,
          variant: "outline",
          to: "/"
        }
      ]}
    />
  );
}

export function EmptySearchResults({ query, onClearSearch }) {
  return (
    <EmptyStateBase
      icon={FiSearch}
      title="No results found"
      description={`We couldn't find anything matching "${query}". Try searching with different keywords or check your spelling.`}
      actions={[
        {
          label: "Clear Search",
          variant: "outline",
          onClick: onClearSearch
        }
      ]}
    />
  );
}

export function EmptyAvailability() {
  return (
    <EmptyStateBase
      icon={FiClock}
      title="Set your availability"
      description="Let your golf groups know when you're free to play. Set your general availability and busy times."
      actions={[
        {
          label: "Set Availability",
          icon: FiClock,
          variant: "primary",
          fullWidth: true
        }
      ]}
    />
  );
}

export function EmptyProfile() {
  return (
    <EmptyStateBase
      icon={FiUser}
      title="Complete your profile"
      description="Add your information to help your golf groups get to know you better and improve your TeeSync experience."
      actions={[
        {
          label: "Update Profile",
          icon: FiUser,
          variant: "primary",
          fullWidth: true
        }
      ]}
    />
  );
}

// Generic empty state for custom use cases
export function EmptyGeneric({ 
  icon = FiMapPin,
  title = "Nothing here yet",
  description = "Get started by adding some content.",
  actionLabel = "Get Started",
  actionIcon = FiPlus,
  onAction,
  actionTo
}) {
  const actions = actionLabel ? [{
    label: actionLabel,
    icon: actionIcon,
    variant: "primary",
    onClick: onAction,
    to: actionTo
  }] : [];

  return (
    <EmptyStateBase
      icon={icon}
      title={title}
      description={description}
      actions={actions}
    />
  );
}

// Mobile-optimized empty state for small screens
export function EmptyStateMobile({ 
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  className = ''
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`text-center py-8 px-4 space-y-6 ${className}`}
    >
      {/* Compact Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto"
      >
        <SafeIcon icon={icon} className="w-8 h-8 text-text-muted" />
      </motion.div>

      {/* Compact Content */}
      <div className="space-y-2">
        <h3 className="text-h4 text-text-primary">{title}</h3>
        <p className="text-body-sm text-text-secondary">{description}</p>
      </div>

      {/* Stacked Actions for Mobile */}
      <div className="space-y-3 w-full max-w-xs mx-auto">
        {primaryAction && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={primaryAction.onClick}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-accent text-white rounded-md text-label hover:bg-interactive-hover transition-all duration-200 "
            
            
          >
            {primaryAction.icon && <SafeIcon icon={primaryAction.icon} className="w-4 h-4" />}
            <span>{primaryAction.label}</span>
          </motion.button>
        )}

        {secondaryAction && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={secondaryAction.onClick}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-border-subtle text-text-secondary rounded-md text-label hover:bg-surface transition-all duration-200"
            
            
          >
            {secondaryAction.icon && <SafeIcon icon={secondaryAction.icon} className="w-4 h-4" />}
            <span>{secondaryAction.label}</span>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

export default {
  EmptyStateBase,
  EmptyGroups,
  EmptyRounds,
  EmptyMembers,
  EmptyNotifications,
  EmptySearchResults,
  EmptyAvailability,
  EmptyProfile,
  EmptyGeneric,
  EmptyStateMobile
};